import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Heading,
  Hide,
  HStack,
  Icon,
  Show,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import type { APIADPlatforms } from "models/types/api-listing";
import { useParams } from "react-router-dom";
import EmptyTable from "assets/icons/medium/empty-table.svg?react";
import { Empty } from "components/empty";
import { FeaturePreferencesConditionalComponent } from "components/feature-preferences/feature-preferences-conditional-component";
import { ScreenWrapper } from "components/screen-wrapper";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { EnquiryStats } from "modules/vendor-portal/screens/performance/components/enquiry-stats";
import { PerformanceAdCampaign } from "modules/vendor-portal/screens/performance/components/performance-ad-campaign";
import { PerformanceTabs } from "modules/vendor-portal/screens/performance/components/performance-tabs";
import { ReachMarketingCta } from "modules/vendor-portal/screens/performance/components/reach-marketing-cta";
import { AD_PLATFORMS } from "modules/vendor-portal/screens/performance/utils/ad-platform-details";
import { mergeTradeMeViews } from "modules/vendor-portal/screens/performance/utils/merge-trade-me-views";
import { hasReachMarketing } from "modules/vendor-portal/utils/locked-routes-config";

export function PerformanceScreen() {
  const { propertyId } = useParams();
  const { isLoading, data } = useQuery(listingsModel.queries.read(propertyId));
  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();

  if (!data) {
    return null;
  }

  const campaigns = mergeTradeMeViews(data.performance?.stats);

  const allAdPlatformIds: APIADPlatforms[] = campaigns
    .filter((stat) => stat.format.id !== "reach")
    .map((stat) => stat.format.id)
    .concat(
      isAgencyFeaturePreferenceEnabled(
        "vendor_portal.functionality.performance.promote_reach_marketing",
      ) || hasReachMarketing(data)
        ? ["reach"] // Always include reach so we can show CTA
        : [],
    );

  const hasCampaignsToShow = allAdPlatformIds.some((id) => !!AD_PLATFORMS[id]);

  const enquiryStats =
    data?.summary?.count_of_enquiries_by_source.value?.length || 0 ? (
      <EnquiryStats
        stats={data?.summary?.count_of_enquiries_by_source.value
          .map((stat) => ({
            id: stat.id,
            value: stat.value,
            label: stat.label,
          }))
          .filter((stat) => stat.value !== 0)}
        isLoading={isLoading}
      />
    ) : null;

  return (
    <ScreenWrapper>
      <Heading size={{ base: "2xl", md: "lg" }} fontWeight={700} as={"h1"}>
        Performance
      </Heading>

      {/* Desktop Layout */}
      <Show above={"md"}>
        <FeaturePreferencesConditionalComponent
          feature={
            "vendor_portal.functionality.graphs.show_enquiries_source_chart"
          }
        >
          {enquiryStats}
        </FeaturePreferencesConditionalComponent>
        {hasCampaignsToShow && (
          <Flex direction={"column"} gap={4}>
            <Heading fontSize={"xl"} as={"h2"}>
              Campaigns
            </Heading>
            <PerformanceTabs
              adCampaigns={campaigns}
              isLoading={isLoading}
              allAdPlatformIds={allAdPlatformIds}
            />
          </Flex>
        )}
      </Show>

      {/* Mobile Layout */}
      <Hide above={"md"}>
        <Tabs variant={"soft-rounded"} size={"md"}>
          <TabList overflowX={"scroll"} mb={8}>
            <Tab whiteSpace={"nowrap"}>Summary</Tab>
            {hasCampaignsToShow && <Tab whiteSpace={"nowrap"}>Campaigns</Tab>}
          </TabList>
          <TabPanels>
            <TabPanel>
              <Flex direction={"column"} gap={8}>
                {enquiryStats}
              </Flex>
            </TabPanel>
            {hasCampaignsToShow && (
              <TabPanel>
                <Accordion defaultIndex={[0]} allowMultiple>
                  <Flex direction={"column"} gap={2}>
                    {allAdPlatformIds.map((id) => {
                      const campaign = campaigns.find(
                        (stat) => stat.format.id === id,
                      );

                      if (!AD_PLATFORMS[id]) {
                        return null;
                      }

                      return (
                        <AccordionItem
                          key={id}
                          borderColor={"gray.200"}
                          borderWidth={"1px"}
                          borderRadius={"10px"}
                          p={2}
                        >
                          <AccordionButton px={1} py={2} h={16}>
                            <HStack as={"span"} flex={1} gap={2}>
                              <Icon
                                as={AD_PLATFORMS[id].logo}
                                boxSize={"32px"}
                                color={"#FFFF"}
                              />
                              <Text
                                fontSize={"xl"}
                                fontWeight={700}
                                lineHeight={"28px"}
                              >
                                {AD_PLATFORMS[id].name}
                              </Text>
                            </HStack>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel
                            p={0}
                            pos={"relative"}
                            overflow={"hidden"}
                            pb={4}
                          >
                            {!campaign && id === "reach" ? (
                              <ReachMarketingCta />
                            ) : !campaign ? (
                              <Empty
                                icon={<Icon as={EmptyTable} boxSize={"auto"} />}
                                emptyMessage={
                                  "Campaign data currently unavailable"
                                }
                                emptyDesc={
                                  "Performance details will appear here once data becomes available"
                                }
                              />
                            ) : (
                              <PerformanceAdCampaign
                                adCampaign={campaign}
                                isLoading={isLoading}
                              />
                            )}
                          </AccordionPanel>
                        </AccordionItem>
                      );
                    })}
                  </Flex>
                </Accordion>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Hide>
    </ScreenWrapper>
  );
}
